import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import { useDialog } from 'core/providers/DialogProvider';
import { useEffect, useState } from 'react';
import { FirebaseFunctionNames } from 'core/constants/firebase-function-names';
import { useFlags } from '@atlaskit/flag';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import { SelectedValue } from 'shared/components/filters/filters';
import { ButtonType } from 'core/enums/button-type';
import { ISharedDialogButton } from 'shared/components/dialog-base/dialog-base-interface';
import { IApiService } from 'core/api/types';

interface IExportDialog {
  title: string;
  apiService: IApiService<any>;
  functionName: FirebaseFunctionNames;
  filterQuery: SelectedValue[];
}

const ExportDialog = ({ title, apiService, functionName, filterQuery = [] }: IExportDialog) => {
  const dialog = useDialog();
  const flags = useFlags();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Record<string, any>>({});

  useEffect(() => {
    const data = filterQuery.reduce((acc, filter) => {
      acc[filter.id] = filter.value;
      return acc;
    }, {} as Record<string, any>);
    setData(data);
  }, [filterQuery]);

  const exportCsv = async () => {
    const csv: string = await apiService.callFunction(data, functionName);
    const blob = new Blob(['\ufeff', csv]);
    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = `pride_pharmacy_${title.toLowerCase()}.csv`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const submit = async () => {
    setLoading(true);
    try {
      await exportCsv();
      showSuccessFlag(`Export successful`, `The ${title.toLowerCase()} were successfully exported`, flags);
      dialog?.closeDialog();
    } catch (error) {
      setLoading(false);
      showErrorFlag('Export failed', 'The export failed, please try again.', flags);
    }
  };

  const customButtons: ISharedDialogButton[] = [
    {
      key: 'cancel',
      type: ButtonType.Button,
      buttonProps: {
        onClick: () => dialog?.closeDialog(),
        appearance: 'subtle',
        label: 'Cancel',
        type: 'button',
      },
    },
    {
      key: 'confirmAction',
      type: ButtonType.Button,
      buttonProps: {
        isLoading: loading,
        onClick: () => submit(),
        appearance: 'primary',
        label: 'Export',
        type: 'button',
      },
    },
  ];

  return (
    <SharedDialogBase
      title={title}
      textContent={'Exporting the data will take into account any active filters you have applied.'}
      customButtons={customButtons}
    />
  );
};

export default ExportDialog;
